import { useState, useRef, useCallback, useEffect } from 'react';
import { Room, RoomEvent, Track, VideoPresets, ConnectionQuality, createLocalTracks, createLocalVideoTrack, createLocalAudioTrack, LocalVideoTrack, LocalAudioTrack } from 'livekit-client';
import { logDiagnostic } from '../services/diagnostics';
import { rawSnapshot, computeDeltaMetrics, summarizeSeverity, extractContext } from '../services/networkStats';

// Intervalo entre amostras de RTCStats. 10s e um compromisso: curto o
// suficiente pra capturar engasgos de rede (ex: elevador), longo o suficiente
// pra nao inundar o banco com linhas (sample gera 2-4 DiagnosticEvent). Vale
// um setting externo se algum dia virar problema.
const NETWORK_SAMPLE_INTERVAL_MS = 10_000;

// LiveKit Disconnect reason code -> label human-friendly pra gravar no diag.
// Valores conhecidos do SDK: 1 CLIENT_INITIATED, 2 DUPLICATE_IDENTITY,
// 3 SERVER_SHUTDOWN, 4 PARTICIPANT_REMOVED, 5 ROOM_DELETED, 6 STATE_MISMATCH,
// 7 JOIN_FAILURE, 8 MIGRATION, 9 SIGNAL_CLOSE, 10 ROOM_CLOSED.
const DISCONNECT_REASON_LABEL = {
  1: 'client_initiated',
  2: 'duplicate_identity',
  3: 'server_shutdown',
  4: 'participant_removed',
  5: 'room_deleted',
  6: 'state_mismatch',
  7: 'join_failure',
  8: 'migration',
  9: 'signal_close',
  10: 'room_closed',
};

// Reasons que indicam encerramento INTENCIONAL da consulta (medico saiu,
// sala foi deletada, sala fechou normalmente). So nesses casos vale postar
// end_meeting pro iframe pai - o pai (HelpGlobal) marca a consulta como
// realizada e fecha o embed. CLIENT_INITIATED (1) nao entra aqui de proposito:
// quando o paciente clica "sair" o EmbedPage decide separadamente o que fazer
// (e o estado serverClosed se refere ao OUTRO lado fechando, nao ao proprio).
//
// O resto (0 unknown, 2 duplicate, 3 server_shutdown, 6 state, 7 join_failure,
// 8 migration, 9 signal_close) sao FALHAS TECNICAS em que o cliente caiu sem
// que ninguem tenha decidido encerrar - tratar como "consulta realizada"
// fazia o iframe pai redirecionar o paciente sem ele ter conectado, gerando
// "clica entrar -> retorna pro inicio -> tenta de novo" reportado em campo.
const INTENTIONAL_END_REASONS = new Set([4, 5, 10]);

// Tipos de candidato ICE relevantes pra triagem:
//   host    -> IP local da maquina (so funciona em LAN)
//   srflx   -> IP publico descoberto via STUN (NAT cone-friendly)
//   prflx   -> peer-reflexive descoberto durante connectivity checks
//   relay   -> mediado por TURN server (necessario em NAT simetrico/CGNAT
//              e em firewalls corporativos que so liberam TCP/443)
// Se nenhum cliente reportar 'relay' por dias seguidos e ainda assim houver
// connect_failed em mobile, e sinal claro que TURN nao esta acessivel pelo
// caminho do cliente (cert invalido, firewall bloqueando, DNS errado etc).

function attachIceDiagnostics(room) {
  // Sondagem dos PCs publisher/subscriber. Em livekit-client 2.x ficam em
  // room.engine.pcManager.{publisher,subscriber}.pc. Defensivo contra mudancas
  // futuras de campo - vasculha algumas formas conhecidas antes de desistir.
  const collectPCs = () => {
    const out = [];
    try {
      const eng = room?.engine;
      const pcm = eng?.pcManager || eng?.client?.pcManager;
      const pub = pcm?.publisher?.pc || eng?.publisher?.pc || pcm?.publisherPC;
      const sub = pcm?.subscriber?.pc || eng?.subscriber?.pc || pcm?.subscriberPC;
      if (pub) out.push(['publisher', pub]);
      if (sub) out.push(['subscriber', sub]);
    } catch { /* sem PCs - sem logging ICE */ }
    return out;
  };

  // iceConnectionState passa por: new -> checking -> connected -> (completed)
  // -> disconnected/failed. O timeout de ~10s entre 'checking' e 'failed' que
  // a paciente do incidente original sofreu fica capturado aqui em tempo real.
  const pcs = collectPCs();
  for (const [role, pc] of pcs) {
    try {
      pc.addEventListener('iceconnectionstatechange', () => {
        const state = pc.iceConnectionState;
        const sev = (state === 'failed' || state === 'disconnected') ? 'warn' : 'info';
        logDiagnostic({
          severity: sev,
          type: 'ice_state',
          message: `ICE ${role} -> ${state}`,
          metadata: { role, state, gathering: pc.iceGatheringState },
        });
      });
    } catch { /* PC ja morto ou listener nao suportado */ }

    // icecandidate: dispara pra cada candidato gerado (host, srflx, relay).
    // Logamos so relay porque sao raros mas decisivos: se nao aparece NENHUM
    // relay quando ha TURN configurado, o browser nao conseguiu falar com o
    // servidor TURN - e o "could not establish pc connection" e por causa
    // disso. Host/srflx aparecem em qualquer rede e poluiriam o log.
    try {
      pc.addEventListener('icecandidate', (ev) => {
        const c = ev.candidate;
        if (!c || c.type !== 'relay') return;
        logDiagnostic({
          severity: 'info',
          type: 'ice_relay_gathered',
          message: `ICE ${role} relay obtido via ${c.relayProtocol || c.protocol || '?'}`,
          metadata: {
            role,
            protocol: c.protocol || null,
            relayProtocol: c.relayProtocol || null,
            // url (na spec moderna) ou relatedAddress diz qual TURN server
            // gerou esse relay - util pra distinguir CF vs coturn proprio.
            url: c.url || null,
            address: c.address || null,
            port: c.port || null,
          },
        });
      });
    } catch { /* sem suporte */ }

    // icecandidateerror: dispara quando o TURN/STUN allocate falha. Aqui esta
    // a resposta definitiva pra "o browser tentou usar o coturn?" - se chega
    // erro 401, 403, 437, 441 etc, browser TENTOU mas servidor recusou. Se
    // erro tipo "Address resolution failed" ou ETIMEDOUT, e DNS/network. Se
    // NAO chega evento nenhum, browser nem tentou (improvavel mas possivel).
    try {
      pc.addEventListener('icecandidateerror', (ev) => {
        logDiagnostic({
          severity: 'warn',
          type: 'ice_candidate_error',
          message: `ICE ${role} candidate error code=${ev.errorCode} ${ev.errorText || ''}`,
          metadata: {
            role,
            errorCode: ev.errorCode || null,
            errorText: ev.errorText || null,
            url: ev.url || null,
            address: ev.address || null,
            port: ev.port || null,
            hostCandidate: ev.hostCandidate || null,
          },
        });
      });
    } catch { /* sem suporte */ }
  }

  // Apos a sessao estabilizar, captura UM snapshot do par de candidatos
  // selecionado. 3s e suficiente: ICE nominal completa em <2s em rede boa,
  // <5s em mobile. Se ainda nao tiver par 'succeeded' nessa janela, a
  // conexao provavelmente esta degradando e o ice_state warn vai aparecer.
  setTimeout(async () => {
    for (const [role, pc] of collectPCs()) {
      try {
        const stats = await pc.getStats();
        const pairs = new Map();
        const cands = new Map();
        let selectedId = null;
        stats.forEach(r => {
          if (r.type === 'candidate-pair') pairs.set(r.id, r);
          else if (r.type === 'local-candidate' || r.type === 'remote-candidate') cands.set(r.id, r);
          else if (r.type === 'transport' && r.selectedCandidatePairId) selectedId = r.selectedCandidatePairId;
        });
        let pair = selectedId ? pairs.get(selectedId) : null;
        if (!pair) {
          // Fallback pro browser que nao popula transport.selectedCandidatePairId
          // (Firefox antigo, alguns WebView). Pega o primeiro succeeded+nominated.
          for (const p of pairs.values()) {
            if (p.state === 'succeeded' && p.nominated) { pair = p; break; }
          }
        }
        if (!pair) continue;
        const local = cands.get(pair.localCandidateId);
        const remote = cands.get(pair.remoteCandidateId);
        logDiagnostic({
          severity: 'info',
          type: 'ice_pair',
          message: `ICE ${role} ${local?.candidateType || '?'}/${remote?.candidateType || '?'} via ${local?.protocol || '?'}`,
          metadata: {
            role,
            localType: local?.candidateType || null,
            remoteType: remote?.candidateType || null,
            protocol: local?.protocol || null,
            // relayProtocol so existe quando localType=='relay' - diz se o
            // TURN ta atravessando UDP, TCP ou TLS. UDP=>TCP=>TLS na ordem
            // de fallback que o cliente tentou.
            relayProtocol: local?.relayProtocol || null,
            // relayAddress: IP do servidor TURN que alocou o relay. Permite
            // ao painel admin agregar uso por provider em produc^ao:
            //   206.42.21.201        -> coturn proprio
            //   IPs anycast CF       -> Cloudflare Realtime
            //   IP do node LiveKit   -> TURN embutido do SFU
            // Nulo quando localType != 'relay'.
            relayAddress: local?.candidateType === 'relay' ? (local?.address || null) : null,
            networkType: local?.networkType || null,
          },
        });
      } catch { /* getStats falhou em algum browser exotico */ }
    }
  }, 3000);
}

/**
 * React wrapper em volta do livekit-client.
 *
 * Expoe os objetos Track do LiveKit diretamente (em vez de sintetizar
 * MediaStream objects). O consumidor passa esses tracks pro <VideoPlayer>
 * que chama track.attach(videoElement) - esse e o metodo idiomatico do SDK
 * e cuida sozinho de:
 *   - iOS Safari autoplay policy (playsInline + muted=false gesture)
 *   - Reatach automatico quando o MediaStreamTrack interno e substituido
 *     durante reconnect (wifi<->4G, ICE restart)
 *   - Cleanup de listeners internos em detach()
 *
 * Tentar fazer isso no braco via srcObject quebra em celular - foi o bug
 * reportado como "paciente ve tela preta, audio OK" apos troca de rede.
 */
export default function useLiveKit({ onEvent } = {}) {
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState(null);
  const [remoteName, setRemoteName] = useState('');
  const [isRemoteConnected, setIsRemoteConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  // Mute state exposed for UI overlays. local* = self, remote* = peer.
  const [localMic, setLocalMic] = useState(true);
  const [localCam, setLocalCam] = useState(true);
  const [remoteMic, setRemoteMic] = useState(true);
  const [remoteCam, setRemoteCam] = useState(true);
  const [cameraFacing, setCameraFacing] = useState('user');

  // Metricas de rede da ultima amostra (bitrate, packet loss, jitter). UI pode
  // opcionalmente mostrar um indicador discreto usando isso; admin usa o log
  // do backend pra historico. E um array de linhas - ver services/networkStats.
  const [networkMetrics, setNetworkMetrics] = useState([]);

  // Contador que incrementa toda vez que o room faz um Reconnected ou
  // SignalReconnected. Componentes que renderizam <video> usam isso como
  // dep do useEffect pra re-chamar track.attach(el) + play() apos o SDK
  // restaurar a conexao. Sem esse "bump" o <video> pode ficar com decoder
  // pausado mesmo quando o MediaStreamTrack interno ja ta vivo de novo
  // (situacao classica da troca wifi<->4G sem refresh).
  const [connectionEpoch, setConnectionEpoch] = useState(0);

  const roomRef = useRef(null);
  const prevSnapshotRef = useRef(null);
  const samplerTimerRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const emit = useCallback((type, data = {}) => {
    try { onEventRef.current?.(type, data); } catch { /* consumer error - swallow */ }
  }, []);

  // Cache de estado de mute por (identity, source). Atualizado APENAS por
  // eventos comprovados do SDK: TrackPublished/Unpublished, TrackMuted/Unmuted,
  // LocalTrackPublished/Unpublished, ParticipantDisconnected. Nunca lemos
  // isMicrophoneEnabled/isCameraEnabled nem pub.isMuted dentro de syncMuteStates
  // porque esses retornam false em duas situacoes ambiguamente:
  //   (a) participante existe mas TrackPublication ainda nao foi registrada
  //       (janela transitoria entre ParticipantConnected e TrackPublished)
  //   (b) o publisher realmente mutou
  // Sem distinguir (a) de (b), o ultimo a entrar na sala aparecia como mutado
  // pro outro lado mesmo com mic/cam ativos - so destravava com toggle off+on
  // que disparava TrackUnmuted real. O cache resolve definitivamente porque
  // so muda em eventos de transicao confirmados, sem janela de incerteza.
  // Chave: `${identity}|${source}` (microphone | camera | screen_share).
  // Valor: true se mutado, false se ativo, ausente se ainda nao publicado.
  const muteStateRef = useRef(new Map());

  const muteKey = (identity, source) => `${identity || ''}|${source || ''}`;

  const setMuteCache = useCallback((identity, source, isMuted) => {
    if (!identity || !source) return;
    muteStateRef.current.set(muteKey(identity, source), !!isMuted);
  }, []);

  const clearMuteCache = useCallback((identity, source) => {
    if (!identity) return;
    if (source) {
      muteStateRef.current.delete(muteKey(identity, source));
      return;
    }
    // Sem source: limpa todas entradas daquele identity (ParticipantDisconnected)
    const prefix = `${identity}|`;
    for (const k of [...muteStateRef.current.keys()]) {
      if (k.startsWith(prefix)) muteStateRef.current.delete(k);
    }
  }, []);

  // Le o estado a partir do cache event-driven em vez do SDK. Devolve booleans
  // estaveis pra UI - sem race com janela transitoria do SDK. Logica em 2
  // niveis pra distinguir corretamente "publicacao nao existe" (sem permissao
  // ou ainda nao publicou) de "publicacao existe e estado e mute/unmute":
  //   1. Se getTrackPublication(...) retorna undefined -> SEM publicacao,
  //      UI mostra mic/cam OFF. Cobre paciente que negou permissao no
  //      browser - antes ele aparecia pro medico com botao verde "Audio
  //      ativo" mesmo sem ter publicado nada, e o paciente clicava no
  //      proprio botao tentando desmutar mas setMicrophoneEnabled(true)
  //      jogava NotAllowedError silencioso e a UI nunca atualizava.
  //   2. Se publicacao existe -> le o cache. Cache vazio = otimista (UI
  //      mostra ON), cobre janela transitoria entre publish e cache
  //      populate sem flicker.
  const syncMuteStates = useCallback((room) => {
    if (!room) return;
    const local = room.localParticipant;
    if (local) {
      // Existencia de publicacao = teve permissao e publicou. Sem pub =
      // negou permissao ou nao publicou ainda - UI deve mostrar OFF.
      const localMicPub = local.getTrackPublication?.(Track.Source.Microphone);
      const localCamPub = local.getTrackPublication?.(Track.Source.Camera);
      if (!localMicPub) {
        setLocalMic(false);
      } else {
        const localMicMuted = local.identity ? muteStateRef.current.get(muteKey(local.identity, 'microphone')) : undefined;
        setLocalMic(localMicMuted === true ? false : true);
      }
      if (!localCamPub) {
        setLocalCam(false);
      } else {
        const localCamMuted = local.identity ? muteStateRef.current.get(muteKey(local.identity, 'camera')) : undefined;
        setLocalCam(localCamMuted === true ? false : true);
      }
    }

    // Remote: mesma logica - itera participantes, usa existencia de
    // publicacao + cache. "Latecomer sem permissao" pro medico aparece
    // imediatamente com badge OFF, em vez de aparecer ativo e enganar.
    let micOn = null, camOn = null, hasRemote = false;
    room.remoteParticipants.forEach(p => {
      hasRemote = true;
      const micPub = p.getTrackPublication?.(Track.Source.Microphone);
      const camPub = p.getTrackPublication?.(Track.Source.Camera);
      if (!micPub) {
        micOn = false;
      } else {
        const micMuted = muteStateRef.current.get(muteKey(p.identity, 'microphone'));
        micOn = micMuted === true ? false : true;
      }
      if (!camPub) {
        camOn = false;
      } else {
        const camMuted = muteStateRef.current.get(muteKey(p.identity, 'camera'));
        camOn = camMuted === true ? false : true;
      }
    });
    if (!hasRemote) {
      setRemoteMic(true);
      setRemoteCam(true);
      return;
    }
    setRemoteMic(prev => micOn === null ? prev : micOn);
    setRemoteCam(prev => camOn === null ? prev : camOn);
  }, []);

  // Picks the current local camera track + remote participant's camera/screen +
  // microphone tracks and writes them to state. Called on any event que pode
  // mudar membership (subscribe/unsubscribe, publish, participant join/leave,
  // reconnect). Screen-share vence camera quando presente.
  //
  // Iteracao em REVERSE (mais recente primeiro): quando um participante
  // reconecta (refresh, troca de rede), o LiveKit server pode demorar ate
  // 5min pra evitar o participante antigo do room.remoteParticipants. Nessa
  // janela, o Map contem D1 (morto) + D2 (vivo) simultaneamente. Iterando
  // mais novo pra mais velho, pegamos o track vivo. Sem isso, o paciente
  // via video congelado por minutos apos o medico dar refresh.
  const syncTracks = useCallback((room) => {
    if (!room) return;

    const localCamPub = room.localParticipant.getTrackPublication?.(Track.Source.Camera);
    setLocalVideoTrack(localCamPub?.track ?? null);

    let video = null, audio = null, name = '', hasRemote = false;
    const participants = Array.from(room.remoteParticipants.values()).reverse();
    for (const p of participants) {
      hasRemote = true;
      if (!name) name = p.name || p.identity || '';
      const screenPub = p.getTrackPublication?.(Track.Source.ScreenShare);
      const camPub = p.getTrackPublication?.(Track.Source.Camera);
      const micPub = p.getTrackPublication?.(Track.Source.Microphone);
      // Pega o track direto, SEM filtrar por isMuted. O filtro
      // (`pub && !pub.isMuted ? pub.track : null`) ja foi reintroduzido e
      // removido varias vezes nesse arquivo - cada iteracao trocou um bug
      // por outro. Estado final: nao filtramos.
      // Por que nao filtrar e correto:
      //   - LiveKit SDK tem janela transitoria onde pub.isMuted=true mesmo
      //     quando o publisher esta unmuted. Nao ha evento quando essa
      //     transicao silenciosa pra false acontece. Filtrar nessa janela
      //     deixava audio=null pra sempre, exigindo o usuario fazer toggle
      //     manual de mic pra forcar TrackUnmuted real e reanexar.
      //   - Mute genuino (publisher mutou de verdade) = silencio na fonte.
      //     Track anexada com mute = audio element em silencio, sem dados,
      //     mas pronto pra retomar quando dados chegarem (quando publisher
      //     desmutar). Browser cuida disso sozinho.
      //   - Publisher que desconectou abruptamente sai de
      //     remoteParticipants via ParticipantDisconnected; o loop acima
      //     nem visita esse identity, entao nao precisa de filtro defensivo.
      // Cobertura do caso "<audio> element fica paused apos attach durante
      // janela transitoria":
      //   - VideoPlayer:78-91 ja chama el.play() explicito apos attach.
      //   - VideoPlayer:143-152 binda 'unmuted' e 'restarted' no proprio
      //     Track do SDK; quando dados comecam a fluir o reattach+play
      //     dispara automatico.
      const cam = camPub?.track ?? null;
      const screen = screenPub?.track ?? null;
      const mic = micPub?.track ?? null;
      if (!video) video = screen ?? cam ?? null;
      if (!audio) audio = mic ?? null;
    }
    setRemoteVideoTrack(video);
    setRemoteAudioTrack(audio);
    setRemoteName(name);
    setIsRemoteConnected(hasRemote);
  }, []);

  // Inicia o sampler periodico de RTCStats. Calcula deltas entre snapshots e
  // dispara um DiagnosticEvent "network_sample" a cada tick. Nao acumula: se
  // chamado de novo, para a rodada anterior antes. Cleanup acontece no
  // disconnect ou no unmount.
  const startNetworkSampling = useCallback((room) => {
    if (samplerTimerRef.current) {
      clearInterval(samplerTimerRef.current);
      samplerTimerRef.current = null;
    }
    prevSnapshotRef.current = null;

    const tick = async () => {
      if (!roomRef.current || roomRef.current !== room) return;
      try {
        const curr = await rawSnapshot(room);
        const prev = prevSnapshotRef.current;
        prevSnapshotRef.current = curr;
        // Primeiro tick serve so de baseline - sem deltas.
        if (!prev) return;

        const metrics = computeDeltaMetrics(prev, curr);
        if (metrics.length === 0) return;

        setNetworkMetrics(metrics);

        const context = extractContext(curr);
        const severity = summarizeSeverity(metrics);
        logDiagnostic({
          severity,
          type: 'network_sample',
          message: `Amostra de rede (${metrics.length} tracks)`,
          metadata: { intervalMs: NETWORK_SAMPLE_INTERVAL_MS, context, metrics },
        });
      } catch (err) {
        // Sampling nao pode quebrar a chamada; log local e segue o jogo.
        console.warn('network sample error:', err);
      }
    };
    tick();
    samplerTimerRef.current = setInterval(tick, NETWORK_SAMPLE_INTERVAL_MS);
  }, []);

  const connect = useCallback(async (token, wsUrl, iceServers, options, backgroundProcessor, preAcquiredStream) => {
    setConnectionStatus('connecting');
    try {
      // iceServers do backend (Cloudflare TURN + coturn proprio) PRECISAM
      // ser agregados ao rtcConfig. Sem TCP fallback no servidor LiveKit
      // (tcp_port removido em infra/livekit/livekit.yaml e docker-compose),
      // pacientes em rede que bloqueia UDP/7882 dependem de TURN-UDP via
      // Cloudflare (que escuta UDP em multiplas portas, incluindo 443) pra
      // ter qualquer caminho de midia. Sem isso, esses pacientes nao
      // conectam de jeito nenhum.
      // Antes da remocao do TCP fallback (cc3b3a6), 0% das chamadas caiam
      // nos TURN porque o caminho TCP/7881 direto sempre cobria - agora
      // que TCP nao existe, TURN volta a ser essencial pra cobertura.
      // options.forceRelay (debug do testIce) seta iceTransportPolicy=relay
      // pra forcar o browser a passar so por TURN, util pra validar TURN.
      const rtcConfig = (Array.isArray(iceServers) && iceServers.length > 0)
        ? {
            iceServers,
            ...(options?.forceRelay ? { iceTransportPolicy: 'relay' } : {}),
          }
        : undefined;
      // Diag: prova objetiva de que o frontend recebeu iceServers do /join e
      // esta passando pro RTCPeerConnection. Se esse evento aparece com
      // serverCount>0 e a chamada falha com "could not establish pc
      // connection" mas SEM nenhum ice_candidate de tipo relay, e sinal de
      // que o browser nao conseguiu sequer atingir os TURNs (firewall/MITM
      // dropando antes do TURN allocate request).
      try {
        logDiagnostic({
          severity: 'info',
          type: 'ice_config',
          message: `RTCPeerConnection config: ${iceServers?.length ?? 0} iceServer entries`,
          metadata: {
            serverCount: iceServers?.length ?? 0,
            urls: iceServers?.flatMap?.(s => s.urls ?? []) ?? [],
            hasTurns443: iceServers?.some?.(s => s.urls?.some?.(u => /turns:.*:443/.test(u))) ?? false,
            hasCloudflare: iceServers?.some?.(s => s.urls?.some?.(u => u.includes('cloudflare.com'))) ?? false,
            hasCoturn: iceServers?.some?.(s => s.urls?.some?.(u => u.includes('franciscooliveira.online'))) ?? false,
          },
        });
      } catch { /* diag nao deve quebrar connect */ }
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        ...(rtcConfig ? { rtcConfig } : {}),
        // O SDK do LiveKit por padrao escuta pagehide/beforeunload/freeze e
        // desconecta sozinho ("Page leave detected, disconnecting"). Isso
        // dispara em falsos positivos quando o painel pai (HelpGlobal) inicia
        // download de anexo do prontuario - o browser dispara pagehide no
        // iframe enquanto avalia se vai virar navegacao ou download. Como o
        // download cancela a "navegacao", o SDK ja desconectou e o medico
        // fica fora da sala ate dar refresh. Tem cleanup de bfcache via
        // pageshow+reload no EmbedPage pra outros cenarios; aqui especifico
        // do anexo, esse override e necessario.
        disconnectOnPageLeave: false,
        videoCaptureDefaults: {
          resolution: VideoPresets.h1080.resolution,
          // Cap em 30fps NA CAPTURA (no device, antes de encodar). Sem isso,
          // device captura nativo (60-70fps em qualquer iPhone/Galaxy/Mac
          // moderno) e o encoder roda 2x o trabalho necessario, saturando
          // uplink em rede media. 'maxFramerate' em videoEncoding nao
          // funciona com simulcast (cada layer encoda na taxa do MST de
          // entrada). Capar na CAPTURA e a unica forma efetiva.
          frameRate: { ideal: 30, max: 30 },
        },
        // VP8 como codec primario: suporte universal (iOS Safari, Android
        // antigo, qualquer desktop). Sem backupCodec porque VP8 ja decoda
        // em todo lugar - nao precisa de fallback. Capture em 1080p +
        // simulcast com 4 camadas (180/360/720/1080) deixa o SFU adaptar.
        publishDefaults: {
          simulcast: true,
          videoCodec: 'vp8',
          videoEncoding: { maxBitrate: 2_000_000, maxFramerate: 30 },
          videoSimulcastLayers: [
            VideoPresets.h180,
            VideoPresets.h360,
            VideoPresets.h720,
            VideoPresets.h1080,
          ],
        },
        // Politica explicita de reconexao. Default do SDK tentava praticamente
        // pra sempre, deixando a UI presa em "Conectando..." sem limite
        // claro em ambientes com ICE ruim. 10 tentativas cobrem wifi<->4G
        // normal + um periodo razoavel de flutuacao; maxDelay=10s evita
        // thundering herd quando varias salas tentam voltar juntas.
        reconnectPolicy: {
          nextRetryDelayInMs: ({ retryCount }) => {
            if (retryCount >= 10) return null; // desiste e dispara Disconnected
            // 1s, 2s, 4s, 8s... ate teto de 10s.
            return Math.min(1000 * Math.pow(2, retryCount), 10_000);
          },
        },
      });

      // prepareConnection: dispara DNS lookup + WS upgrade + ICE candidate
      // gathering em PARALELO com a registracao dos handlers abaixo. Quando
      // o room.connect() rodar, a conexao ja esta aquecida e o LiveKit
      // reaproveita - ganho de 200-500ms na maioria dos casos, e elimina o
      // sintoma "1a tentativa falha com 'could not establish pc connection'"
      // que afeta Safari (iOS/macOS) com timeout de ICE agressivo. Sem await
      // de proposito: queremos paralelismo, nao serializacao. Falha no
      // prewarm nao bloqueia o connect real - o SDK recomeca o gathering la.
      try {
        room.prepareConnection(wsUrl, token)?.catch?.(() => { /* falha de prewarm nao bloqueia connect */ });
      } catch { /* SDK antigo sem prepareConnection - cai pro flow padrao */ }

      roomRef.current = room;

      const refresh = () => { syncTracks(room); syncMuteStates(room); };

      // Forca uma nova subscription em todos os tracks remotos. Uso pos-
      // reconnect quando o SFU pode ter perdido state de subscription e a
      // gente fica com track "vivo mas sem frame" - toggle setSubscribed
      // false->true faz o servidor mandar um keyframe novo e o decoder do
      // cliente destrava. Delay de 150ms entre off e on da tempo do SDK
      // limpar o estado interno antes de re-assinar.
      const resubscribeAllRemotes = () => {
        try {
          room.remoteParticipants.forEach(p => {
            p.trackPublications.forEach(pub => {
              if (!pub.isSubscribed) return;
              try {
                pub.setSubscribed(false);
                setTimeout(() => {
                  try { pub.setSubscribed(true); } catch { /* pub pode ter sumido */ }
                }, 150);
              } catch { /* SDK antigo sem setSubscribed */ }
            });
          });
        } catch { /* defensivo - nao quebra o reconnect flow */ }
      };

      room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
        // Nao forca VideoQuality.HIGH aqui de proposito: isso sobrescreveria a
        // decisao do adaptiveStream que olha o tamanho do <video> no DOM e a
        // banda do subscriber pra escolher a camada do simulcast. Forcar HIGH
        // fazia o servidor sempre mandar 720p pesado mesmo pra paciente em 4G
        // - encoder encaixava, audio/video dessincronizavam. adaptiveStream
        // cuida disso corretamente quando deixado sozinho.
        if (track.source === Track.Source.ScreenShare) {
          emit('screenshare_started', { identity: participant?.identity, name: participant?.name });
        }
        refresh();

        // Bump de connectionEpoch em audio TrackSubscribed REMOVIDO.
        // Telemedicinanew so chama buildRemoteStream(room) - sem reattach
        // forcado pos-attach inicial. O bump aqui disparava o effect 4 do
        // VideoPlayer (srcObject=null + attach + play) microsegundos depois
        // do attach inicial do effect 2 - dois attachs em sequencia podiam
        // confundir o browser e deixar o <audio> em estado inconsistente,
        // colaborando com o sintoma "primeiro audio fica mudo". O reattach
        // continua acontecendo via listeners 'unmuted'/'restarted' no proprio
        // Track (VideoPlayer effect 3), so que reativos - quando dados de
        // fato voltam a fluir, nao especulativos.

        // Recovery REATIVO de decoder preso. Substitui o resubscribe preemptivo
        // que existia em ParticipantConnected. Cenario coberto: paciente entrou
        // sozinho, passou por signal-reconnect silencioso, medico chega depois
        // - decoder do subscriber fica esperando keyframe que o SFU nao manda
        // sozinho, tile fica preto. Em vez de pagar o custo de renegociacao em
        // TODA entrada de remoto (que quebrava iOS Safari/TCP com renegociacao
        // dupla, derrubando paciente fragil), checamos 3s depois se chegou
        // frame. Sem frame = decoder preso = toggle subscribed pede keyframe.
        // Conexao saudavel: nunca entra no if. Conexao com bug original:
        // detecta sintoma e recupera. Conexao fragil: nao paga o custo extra.
        if (track.kind === 'video' && pub?.setSubscribed) {
          setTimeout(async () => {
            try {
              const stats = await track.getStats();
              let framesDecoded = 0;
              stats.forEach(s => {
                if (s.type === 'inbound-rtp') framesDecoded = s.framesDecoded || 0;
              });
              if (framesDecoded === 0 && pub.isSubscribed) {
                logDiagnostic({
                  severity: 'warn',
                  type: 'stuck_decoder_recovered',
                  message: 'Sem frames apos 3s, forcando resubscribe',
                  metadata: { identity: participant?.identity, source: String(track.source) },
                });
                try {
                  pub.setSubscribed(false);
                  setTimeout(() => { try { pub.setSubscribed(true); } catch { /* pub sumiu */ } }, 150);
                } catch { /* pub ja morto */ }
              }
            } catch { /* getStats nao suportado ou track destruida */ }
          }, 3000);
        }

      });

      // TrackStreamStateChanged: servidor sinaliza que a entrega do track
      // saiu de 'paused' pra 'active' (ou vice-versa). Quando volta pra
      // active pos-interrupcao de rede, bumpa o epoch pra o <video> reataque
      // e o decoder retome - sem isso o tile fica no ultimo frame
      // congelado mesmo com keyframe chegando do servidor.
      room.on(RoomEvent.TrackStreamStateChanged, (_pub, streamState) => {
        // streamState e "active" | "paused". So reagimos ao retorno.
        const active = streamState === 'active' || streamState?.toString?.() === 'active';
        if (!active) return;
        setConnectionEpoch(e => e + 1);
      });
      room.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
        if (track.source === Track.Source.ScreenShare) {
          emit('screenshare_stopped', { identity: participant?.identity, name: participant?.name });
        }
        refresh();
      });
      // TrackPublished chega microsegundos depois de ParticipantConnected, antes
      // disso isCameraEnabled ainda e false - sem o refresh aqui, a outra ponta
      // apareceria inicialmente como "Camera desativada" ate algum toggle.
      // Quando uma TrackPublication remota nasce, marcamos no cache como NAO
      // mutada (default otimista). Esse default e correto porque o caso normal
      // e publisher publicar com mic/cam ativos; se de fato estiver mutado,
      // TrackMuted vai chegar logo em seguida e atualizar o cache. O importante
      // e nao deixar o estado em "indefinido" (que viraria muted=null no
      // syncMuteStates) - ai o ultimo a entrar evita o bug de aparecer mutado.
      room.on(RoomEvent.TrackPublished, (pub, participant) => {
        if (participant?.identity && pub?.source) {
          setMuteCache(participant.identity, pub.source, false);
        }
        refresh();
      });
      room.on(RoomEvent.TrackUnpublished, (pub, participant) => {
        if (participant?.identity && pub?.source) {
          clearMuteCache(participant.identity, pub.source);
        }
        refresh();
      });
      room.on(RoomEvent.LocalTrackPublished, (pub) => {
        if (pub.source === Track.Source.ScreenShare) emit('screenshare_started', { local: true });
        // Camera/mic entrou no ar com sucesso: log info pra o admin conseguir
        // confirmar que o paciente deu permissao de verdade (antes desse log,
        // paciente sem permissao aparecia apenas como "Conectado" sem evento).
        if (pub.source === Track.Source.Camera || pub.source === Track.Source.Microphone) {
          logDiagnostic({
            severity: 'info',
            type: 'track_published',
            message: `Publicou ${pub.source}`,
            metadata: { source: pub.source, kind: pub.kind, sid: pub.trackSid },
          });
        }

        // Instrumentacao do publisher: a cada 5s nos primeiros 30s pos-publish
        // de mic, loga estado completo do outbound. Permite correlacionar com
        // o subscriber audio_health_sample do outro lado: se publisher mostra
        // packetsSent/audioLevel = 0, o problema e mic local. Se publisher
        // OK mas subscriber nao recebe = SFU/network. Se ambos OK mas
        // subscriber audio nao toca = problema do <audio> element.
        if (pub.source === Track.Source.Microphone) {
          const audioTrack = pub.track;
          const trackSid = pub.trackSid;
          let pubSamples = 0;
          const pubInterval = setInterval(async () => {
            pubSamples += 1;
            if (pubSamples > 6 || !audioTrack) { clearInterval(pubInterval); return; }
            try {
              const stats = await audioTrack.getStats();
              let outboundRtp = null;
              let mediaSource = null;
              stats.forEach(s => {
                if (s.type === 'outbound-rtp' && (!outboundRtp || s.kind === 'audio')) outboundRtp = s;
                else if (s.type === 'media-source') mediaSource = s;
              });
              const m = audioTrack.mediaStreamTrack;
              logDiagnostic({
                severity: 'info',
                type: 'audio_health_publisher',
                message: `Audio health sample #${pubSamples} (publisher mic)`,
                metadata: {
                  sample: pubSamples,
                  elapsed_ms: pubSamples * 5000,
                  trackSid,
                  // Local MediaStreamTrack state
                  mstMuted: m?.muted,
                  mstEnabled: m?.enabled,
                  mstReadyState: m?.readyState,
                  trackIsMuted: audioTrack.isMuted,
                  // Outbound RTP stats (publisher sending audio)
                  packetsSent: outboundRtp?.packetsSent ?? null,
                  bytesSent: outboundRtp?.bytesSent ?? null,
                  retransmittedPacketsSent: outboundRtp?.retransmittedPacketsSent ?? null,
                  targetBitrate: outboundRtp?.targetBitrate ?? null,
                  // Media source (mic input) stats - mostra se o mic esta
                  // realmente captando som
                  mediaSourceAudioLevel: mediaSource?.audioLevel ?? null,
                  mediaSourceTotalAudioEnergy: mediaSource?.totalAudioEnergy ?? null,
                  mediaSourceTotalSamplesDuration: mediaSource?.totalSamplesDuration ?? null,
                },
              });
            } catch { /* getStats nao suportado ou track destruida */ }
          }, 5000);
        }

        // Cache local: marca a propria publicacao como nao mutada quando ela
        // sai. Sem isso, syncMuteStates leria undefined e mostraria localMic/Cam
        // como off ate o usuario fazer toggle - sintoma "entrei mas o botao
        // diz que to mutado".
        const localId = room.localParticipant?.identity;
        if (localId && pub?.source) setMuteCache(localId, pub.source, false);
        refresh();
      });
      room.on(RoomEvent.LocalTrackUnpublished, (pub) => {
        if (pub.source === Track.Source.ScreenShare) emit('screenshare_stopped', { local: true });
        const localId = room.localParticipant?.identity;
        if (localId && pub?.source) clearMuteCache(localId, pub.source);
        refresh();
      });
      // ConnectionQualityChanged e o sinal semaforico do LiveKit: Excellent/Good/
      // Poor/Lost por participante. Informacao qualitativa, complementa os stats
      // numericos do sampler. Log so pra os momentos que degradam (Poor/Lost) pra
      // nao bater no banco toda vez que melhora 1 chanfro.
      room.on(RoomEvent.ConnectionQualityChanged, async (quality, participant) => {
        const isLocal = participant === room.localParticipant;
        const label = quality === ConnectionQuality.Excellent ? 'excellent'
                    : quality === ConnectionQuality.Good ? 'good'
                    : quality === ConnectionQuality.Poor ? 'poor'
                    : quality === ConnectionQuality.Lost ? 'lost'
                    : 'unknown';
        if (label === 'poor' || label === 'lost') {
          logDiagnostic({
            severity: label === 'lost' ? 'error' : 'warn',
            type: 'quality_degraded',
            message: `Qualidade ${label} em ${isLocal ? 'self' : participant?.identity}`,
            metadata: { quality: label, identity: participant?.identity, isLocal },
          });
        }

        // Adaptacao dinamica de framerate de captura local com base na
        // qualidade da conexao do PROPRIO publisher. So roda quando o evento
        // e sobre nos mesmos (isLocal). Sob rede ruim, derruba o fps -
        // encoder roda menos, bitrate cai pela metade, fila TCP/UDP do uplink
        // do paciente/medico esvazia, RTT/jitter baixam, audio para de cortar.
        // Sob rede boa, restaura 30fps pra fluidez ideal.
        // Confirmado seguro com simulcast/dynacast: applyConstraints na
        // source track propaga pra todas as layers automaticamente.
        // Issue conhecida: iOS Safari ignora silenciosamente esse constraint
        // (limitacao WebKit, nao crash) - degradacao gracefull, sem bug.
        // NAO usar restartTrack (issue livekit/client-sdk-js#1434 - ignora
        // user constraints em restart). applyConstraints direto e mais
        // previsivel.
        if (!isLocal) return;
        const camPub = room.localParticipant?.getTrackPublication?.(Track.Source.Camera);
        const mst = camPub?.track?.mediaStreamTrack;
        if (!mst) return;
        let targetFps;
        if (quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost) {
          targetFps = 15;  // Rede ruim: economiza encoder + banda agressivamente
        } else if (quality === ConnectionQuality.Good) {
          targetFps = 24;  // Intermediario: cinema-grade, suave em talking-head
        } else {
          targetFps = 30;  // Excellent: full
        }
        try {
          await mst.applyConstraints({ frameRate: { max: targetFps } });
        } catch { /* iOS Safari ou device sem suporte ao constraint - ok */ }
      });
      room.on(RoomEvent.ParticipantConnected, (p) => {
        emit('participant_join', { identity: p.identity, name: p.name });
        refresh();
        // Antes esse handler bumpava connectionEpoch e disparava
        // resubscribeAllRemotes() 500ms depois pra cobrir o cenario
        // "paciente sozinho -> signal reconnect silencioso -> medico chega
        // -> tile preto". Mas isso impunha renegociacao em CADA entrada de
        // remoto, e em conexoes fragis (iOS Safari sob TCP fallback,
        // renegociacao dupla com a que o SFU ja faz pro novo participante)
        // a sessao do paciente caia, levando ele de volta pro device check.
        // Solucao: o recovery foi movido pro TrackSubscribed e e REATIVO -
        // so dispara se realmente nao chegar frame em 3s. Conexao saudavel
        // nao paga custo extra; bug original continua coberto pelo sintoma.
      });
      room.on(RoomEvent.ParticipantDisconnected, (p) => {
        emit('participant_leave', { identity: p.identity, name: p.name });
        // Limpa todas as entradas do cache pra esse identity (mic, cam, screen).
        // Sem isso, se um paciente sair e outro entrar com mesmo identity (raro
        // mas possivel via DUPLICATE_IDENTITY), o cache do anterior contamina
        // o novo.
        if (p?.identity) clearMuteCache(p.identity);
        refresh();
      });
      room.on(RoomEvent.TrackMuted, (pub, participant) => {
        if (participant?.identity && pub?.source) {
          setMuteCache(participant.identity, pub.source, true);
        }
        syncMuteStates(room);
        if (participant !== room.localParticipant) return;
        if (pub.source === Track.Source.Microphone) emit('mic_toggle', { enabled: false });
        if (pub.source === Track.Source.Camera) emit('camera_toggle', { enabled: false });
      });
      room.on(RoomEvent.TrackUnmuted, (pub, participant) => {
        if (participant?.identity && pub?.source) {
          setMuteCache(participant.identity, pub.source, false);
        }
        syncMuteStates(room);
        if (participant === room.localParticipant) {
          if (pub.source === Track.Source.Microphone) emit('mic_toggle', { enabled: true });
          if (pub.source === Track.Source.Camera) emit('camera_toggle', { enabled: true });
          return;
        }
        // Track REMOTO desmutou (medico/paciente ligou camera/mic de novo
        // apos ter desligado). Em Chrome/Safari o decoder do <video> retoma
        // os frames sozinho - mas em webviews restritivos (Instagram, etc) o
        // decoder fica preso no ultimo frame mesmo com a track produzindo
        // frames de novo. Bump do connectionEpoch dispara o Effect 4 do
        // VideoPlayer, que faz srcObject=null + reattach + play() - isso
        // destrava o decoder. Em browsers normais e idempotente.
        // Para audio nao precisa: VideoPlayer tem listener 'unmuted' no
        // proprio Track que ja chama reattach quando dados voltam a fluir.
        if (pub.source === Track.Source.Camera || pub.source === Track.Source.ScreenShare) {
          setConnectionEpoch(e => e + 1);
        }
      });
      room.on(RoomEvent.Reconnected, () => {
        setConnectionStatus('connected');
        // Tracks remotos podem ter sido republicados durante a reconexao: o
        // SDK emite TrackSubscribed de novo nesse caso e o refresh abaixo ja
        // atualiza as refs. track.attach no VideoPlayer re-aponta o elemento
        // <video> pro novo MediaStreamTrack sem que a gente precise manipular
        // srcObject ou fazer toggle de subscription.
        refresh();
        // Forca o servidor a mandar keyframe novo em todos os tracks remotos.
        // Sintoma sem isso: reconectei, vejo o peer mas o video fica congelado
        // no ultimo frame porque o decoder perdeu referencia e esta esperando
        // keyframe que o servidor nao vai mandar sem um pedido explicito.
        resubscribeAllRemotes();
        // Bump do epoch forca o <video> a re-attach + play(). Necessario
        // pra desempacar o decoder em casos onde a reconexao manteve o
        // mesmo Track object (ICE restart) - sem mudanca de referencia o
        // useEffect do VideoPlayer nao dispara sozinho.
        setConnectionEpoch(e => e + 1);
        logDiagnostic({
          severity: 'info',
          type: 'reconnected',
          message: 'LiveKit reconectou',
        });
      });
      // SignalReconnected: o WebSocket voltou mas a sessao de media foi
      // preservada. Mesmo bump + resubscribe pra garantir keyframe fresco.
      room.on(RoomEvent.SignalReconnected, () => {
        resubscribeAllRemotes();
        setConnectionEpoch(e => e + 1);
      });
      room.on(RoomEvent.Disconnected, (reason) => {
        setConnectionStatus('disconnected');
        // Distingue 3 cenarios pelo reason code:
        //   1. CLIENT_INITIATED - o proprio cliente saiu (botao sair, fechou aba).
        //      O EmbedPage trata em outro lugar; aqui nem isFailure nem serverClosed.
        //   4/5/10 - encerramento INTENCIONAL pelo outro lado (medico expulsou,
        //      sala deletada, sala fechou normalmente). serverClosed=true: o
        //      EmbedPage posta end_meeting pro iframe pai marcar a consulta
        //      como realizada e redirecionar o paciente.
        //   resto (0,2,3,6,7,8,9) - FALHA TECNICA (ICE timeout, signal close,
        //      join failure, etc). isFailure=true: o EmbedPage NAO posta
        //      end_meeting - o livekit-client ja tem reconnectPolicy que vai
        //      tentar de volta sozinho. Antes essa logica tratava qualquer
        //      reason!=1 como fim, fazendo o iframe pai (HelpGlobal) fechar
        //      o embed em qualquer drop momentaneo - paciente caia, era
        //      jogada pra tela "Entrar" e tinha que clicar 2-3x ate uma
        //      tentativa nao falhar nos primeiros segundos.
        const serverClosed = INTENTIONAL_END_REASONS.has(reason);
        const isFailure = reason !== 1 && !serverClosed;
        emit('room_closed', { reason, serverClosed, isFailure });
        if (reason !== 1) {
          logDiagnostic({
            severity: isFailure ? 'error' : 'warn',
            type: 'disconnected',
            message: `LiveKit Disconnected code=${reason} (${DISCONNECT_REASON_LABEL[reason] || 'unknown'})`,
            metadata: { reason, label: DISCONNECT_REASON_LABEL[reason] || null, isFailure, serverClosed },
          });
        }
      });
      room.on(RoomEvent.Reconnecting, () => {
        logDiagnostic({
          severity: 'warn',
          type: 'reconnecting',
          message: 'LiveKit tentando reconectar',
        });
      });
      room.on(RoomEvent.SignalReconnecting, () => {
        logDiagnostic({
          severity: 'warn',
          type: 'signal_reconnecting',
          message: 'LiveKit signal reconnecting',
        });
      });
      room.on(RoomEvent.TrackSubscriptionFailed, (trackSid, participant) => {
        logDiagnostic({
          severity: 'error',
          type: 'track_subscription_failed',
          message: `Falha ao subscribir track ${trackSid}`,
          metadata: { trackSid, participant: participant?.identity },
        });
      });
      room.on(RoomEvent.MediaDevicesError, (err) => {
        logDiagnostic({
          severity: 'error',
          type: 'media_devices_error',
          message: err?.message || 'MediaDevicesError',
          metadata: { name: err?.name },
        });
      });

      // peerConnectionTimeout: 7000ms (default do SDK e 15000ms). Em rede
      // saudavel ICE pareia em 1-3s, entao 7s ja e folgado. Em rede com
      // UDP bloqueado, ICE nao vai pareiar nunca via UDP - melhor falhar
      // em 7s e deixar o auto-retry silencioso (EmbedPage) tentar de novo,
      // que ai pega TCP rapido. Reduz tempo entre clique e "conectado" de
      // ~18s pra ~9-10s no caso ruim. Conexoes boas nao pagam custo nenhum.
      await room.connect(wsUrl, token, { peerConnectionTimeout: 7000 });

      // ICE diagnostics: anexa logging nas RTCPeerConnections internas pra
      // capturar transicoes de iceConnectionState e o tipo do candidate-pair
      // selecionado. E API privada do livekit-client v2 (room.engine.pcManager),
      // entao tudo fica em try/catch - se a versao mudar a forma de expor os
      // PCs, perdemos esse sinal mas o restante do connect continua intacto.
      //
      // Por que isso importa: o sintoma "could not establish pc connection"
      // sem mais contexto deixa cego se a causa e CGNAT (precisa relay/TURN),
      // firewall (precisa TURN/TLS na 443), ou perda de rede pos-conexao
      // (failed depois de connected). Logar localType/remoteType (host, srflx,
      // relay, prflx) responde isso direto.
      attachIceDiagnostics(room);

      // Fluxo manual em vez de enableCameraAndMicrophone() pra poder aplicar
      // o VirtualBackground processor ANTES do publish. Causa raiz do bug
      // "primeiro a entrar nao escuta o segundo": setProcessor numa track ja
      // publicada disparava renegociacao SDP no publisher, que com
      // singlePeerConnection=true (default em livekit-client 2.x) bagunca o
      // estado dos transceivers e faz subscriptions subsequentes (audio do
      // peer que entra depois) chegarem com MST stuck muted. Aplicando o
      // processor ANTES do publish, a track ja sai pronta - sem
      // renegociacao depois.
      //
      // createLocalTracks faz 1 getUserMedia({audio, video}) combinado -
      // mesmo padrao do enableCameraAndMicrophone(). Evita 2 calls
      // sequenciais que em drivers Android lentos (Samsung K e similares)
      // dispara NotReadableError quando o handle anterior do deviceCheck
      // ainda nao foi liberado. Se o combinado falhar (ex: camera ocupada
      // mas mic ok), cai no fallback de calls separadas - audio ainda
      // entra mesmo sem video.
      let videoTrack = null;
      let audioTrack = null;
      const camOptions = {
        resolution: VideoPresets.h1080.resolution,
        frameRate: { ideal: 30, max: 30 },
        // facingMode: 'user' (camera frontal) e o padrao em videochamada -
        // Zoom/Meet/Teams fazem isso. Sem essa flag, browsers Android
        // escolhem a camera "principal" do device, que e geralmente a
        // TRASEIRA. Resultado: capa do celular cobrindo a lente, lente
        // suja, ou driver da traseira em uso por outro app -> NotReadableError
        // ou stream sem frame. Caso confirmado em prod (paciente relatou
        // que tinha que trocar manual de traseira pra frontal pra camera
        // funcionar). switchCameraFacing pode trocar pra 'environment'
        // quando o medico precisar.
        facingMode: 'user',
      };
      // Caminho 1: stream pre-adquirido pelo deviceCheck do EmbedPage
      // (paciente). Reusa os MediaStreamTracks ja abertos em vez de chamar
      // getUserMedia de novo. Em Android pos-failed-room.connect, a 2a
      // aquisicao da camera retornava NotReadableError em ~5% das chamadas
      // (driver nao reabre cleanly em ~30s+ apos stop). Reusando, evitamos
      // a janela toda.
      // Caminho 2: sem stream pre-adquirido (medico, ou paciente que
      // skippou deviceCheck por ter permissao cacheada). Cai no
      // createLocalTracks padrao do SDK.
      if (preAcquiredStream) {
        const videoMST = preAcquiredStream.getVideoTracks()[0];
        const audioMST = preAcquiredStream.getAudioTracks()[0];
        try {
          if (videoMST) {
            videoTrack = new LocalVideoTrack(videoMST, undefined, true);
            // CRITICO: source default e Unknown quando envelopamos manualmente.
            // Sem isso, isCameraEnabled/isMicrophoneEnabled retornam false,
            // UI mostra cam+mic off mesmo publicados, e o toggle do usuario
            // chama setCameraEnabled/setMicrophoneEnabled que - como nao acha
            // track de source Camera/Microphone - dispara getUserMedia novo,
            // criando duas tracks paralelas. No Android, getUserMedia({video})
            // pode derrubar handle do mic ja ativo. Setar source explicito
            // faz o SDK reconhecer as tracks como Camera/Microphone canonicas.
            videoTrack.source = Track.Source.Camera;
          }
        } catch (e) {
          logDiagnostic({
            severity: 'warn',
            type: 'initial_camera_failed',
            message: e?.message || 'Falha ao envelopar videoTrack pre-adquirido',
            metadata: { name: e?.name },
          });
        }
        try {
          if (audioMST) {
            audioTrack = new LocalAudioTrack(audioMST, undefined, true);
            audioTrack.source = Track.Source.Microphone;
          }
        } catch (e) {
          logDiagnostic({
            severity: 'warn',
            type: 'initial_mic_failed',
            message: e?.message || 'Falha ao envelopar audioTrack pre-adquirido',
            metadata: { name: e?.name },
          });
        }
      } else {
        try {
          const tracks = await createLocalTracks({ audio: true, video: camOptions });
          for (const t of tracks) {
            if (t.kind === Track.Kind.Video) videoTrack = t;
            else if (t.kind === Track.Kind.Audio) audioTrack = t;
          }
        } catch (combinedErr) {
          logDiagnostic({
            severity: 'warn',
            type: 'create_local_tracks_combined_failed',
            message: combinedErr?.message || 'createLocalTracks combinado falhou, tentando separado',
            metadata: { name: combinedErr?.name },
          });
          try { videoTrack = await createLocalVideoTrack(camOptions); }
          catch (vErr) {
            logDiagnostic({
              severity: 'warn',
              type: 'initial_camera_failed',
              message: vErr?.message || 'Falha ao criar track de camera',
              metadata: { name: vErr?.name, recoverable: vErr?.name !== 'NotAllowedError' },
            });
          }
          try { audioTrack = await createLocalAudioTrack(); }
          catch (aErr) {
            logDiagnostic({
              severity: 'warn',
              type: 'initial_mic_failed',
              message: aErr?.message || 'Falha ao criar track de microfone',
              metadata: { name: aErr?.name, recoverable: aErr?.name !== 'NotAllowedError' },
            });
          }
        }
      }
      // Aplica processor ANTES do publish - esse e o ponto critico do fix
      // do bug do audio. Renegociar pra adicionar processor numa track ja
      // publicada baguncava transceivers e fazia subscriber do peer chegar
      // com MST stuck muted.
      if (videoTrack && backgroundProcessor) {
        try { await videoTrack.setProcessor(backgroundProcessor); }
        catch (procErr) {
          logDiagnostic({
            severity: 'warn',
            type: 'background_processor_failed',
            message: procErr?.message || 'Falha ao aplicar background pre-publish',
            metadata: { name: procErr?.name },
          });
        }
      }
      let initialCamOk = false;
      let initialMicOk = false;
      if (videoTrack) {
        try {
          await room.localParticipant.publishTrack(videoTrack);
          initialCamOk = true;
        } catch (e) {
          logDiagnostic({
            severity: 'warn',
            type: 'initial_camera_publish_failed',
            message: e?.message || 'Falha ao publicar camera',
            metadata: { name: e?.name },
          });
        }
      }
      if (audioTrack) {
        try {
          await room.localParticipant.publishTrack(audioTrack);
          initialMicOk = true;
        } catch (e) {
          logDiagnostic({
            severity: 'warn',
            type: 'initial_mic_publish_failed',
            message: e?.message || 'Falha ao publicar microfone',
            metadata: { name: e?.name },
          });
        }
      }
      // Pre-popular o cache de mute baseado em EXISTENCIA da publicacao
      // (getTrackPublication), nao no getter agregado isMicrophoneEnabled
      // que tem race com o publish. Se a publicacao existe no
      // localParticipant.trackPublications, foi publicada com sucesso;
      // pub.isMuted pode estar transitorio em true mas a fonte de verdade
      // pra "tem track" e a existencia da pub. Pre-populando como nao mutado,
      // o syncMuteStates abaixo le valor correto sem depender da ordem dos
      // eventos LocalTrackPublished.
      const localId = room.localParticipant?.identity;
      if (localId) {
        const micPub = room.localParticipant.getTrackPublication?.(Track.Source.Microphone);
        const camPub = room.localParticipant.getTrackPublication?.(Track.Source.Camera);
        // Pub existe = publicado e nao mutado (default). Pub ausente =
        // permissao negada ou nao publicou - marca como mutado pra UI
        // mostrar botao off corretamente.
        setMuteCache(localId, Track.Source.Microphone, !micPub);
        setMuteCache(localId, Track.Source.Camera, !camPub);
      }

      refresh();
      setConnectionStatus('connected');
      logDiagnostic({
        severity: 'info',
        type: 'session_connected',
        message: 'Conectado a sala',
        metadata: {
          wsUrl: (wsUrl || '').replace(/\?.*$/, ''), // tira query com token sensivel
          hasMic: initialMicOk,
          hasCam: initialCamOk,
        },
      });

      startNetworkSampling(room);
    } catch (err) {
      // So cai aqui agora se room.connect() em si falhou (auth, rede, sala
      // inexistente no LiveKit). Erro de camera/mic nao vem mais pra ca.
      console.error('LiveKit connect error:', err);
      setConnectionStatus('failed');
      logDiagnostic({
        severity: 'error',
        type: 'connect_failed',
        message: err?.message || 'LiveKit connect error',
        metadata: {
          name: err?.name,
          stack: err?.stack,
          wsUrl: (wsUrl || '').replace(/\?.*$/, ''),
        },
      });
      // Limpa o roomRef pra que o caller possa tentar de novo sem ficar
      // com referencia pra um Room object morto.
      try { roomRef.current?.disconnect?.(); } catch { /* ja morto */ }
      roomRef.current = null;
      // Rethrow pra o caller (EmbedPage.handleJoin) saber que falhou e nao
      // colocar o paciente numa UI de reuniao sobre um engine morto. Antes
      // sem o throw, o paciente caia em setJoined(true) com room.state =
      // disconnected e qualquer toggle de camera/mic falhava com
      // "publishing rejected as engine not connected within timeout".
      throw err;
    }
  }, [syncTracks, syncMuteStates, emit, startNetworkSampling]);

  const disconnect = useCallback(() => {
    if (samplerTimerRef.current) {
      clearInterval(samplerTimerRef.current);
      samplerTimerRef.current = null;
    }
    prevSnapshotRef.current = null;
    const room = roomRef.current;
    if (room) {
      room.disconnect();
      roomRef.current = null;
    }
    setLocalVideoTrack(null);
    setRemoteVideoTrack(null);
    setRemoteAudioTrack(null);
    setIsRemoteConnected(false);
    setConnectionStatus('disconnected');
    setNetworkMetrics([]);
  }, []);

  // toggleCamera/toggleMic envolve getUserMedia + SDP renegotiation no SFU.
  // Qualquer um dos dois pode falhar: paciente negou permissao de novo,
  // device esta ocupado, rede vacilou no momento da negociacao, etc.
  // Propagar a excecao daqui pra cima derrubava o paciente da sala: o SDK
  // do LiveKit em 2.x, ao ver a publish falhar no meio, as vezes desconecta
  // pra "limpar" e o WebRTC nao recupera sozinho. Com try/catch em volta,
  // o estado da sala fica intacto e o usuario so ve o botao nao respondendo.
  // Le o estado atual do source local a partir do cache. Fallback pro getter
  // do SDK quando o cache ainda nao foi populado (caso muito antes de
  // LocalTrackPublished firar). Usado pelo toggleMic/toggleCamera pra
  // decidir o target sem depender do getter agregado que tem race com publish.
  const isLocalSourceEnabled = useCallback((room, source) => {
    const localId = room?.localParticipant?.identity;
    if (!localId) return false;
    const cached = muteStateRef.current.get(muteKey(localId, source));
    if (cached === true) return false;   // sabemos que esta mutado
    if (cached === false) return true;   // sabemos que esta nao mutado
    // Sem info no cache - fallback pro getter SDK (raro, so antes do
    // LocalTrackPublished firar).
    if (source === Track.Source.Microphone) return !!room.localParticipant.isMicrophoneEnabled;
    if (source === Track.Source.Camera) return !!room.localParticipant.isCameraEnabled;
    return false;
  }, []);

  const toggleCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const enabled = isLocalSourceEnabled(room, Track.Source.Camera);
    try {
      await room.localParticipant.setCameraEnabled(!enabled);
    } catch (err) {
      logDiagnostic({
        severity: 'error',
        type: 'toggle_camera_failed',
        message: err?.message || 'Falha ao alternar camera',
        metadata: {
          name: err?.name,
          target: !enabled,
          roomState: room.state,
        },
      });
      syncTracks(room);
      syncMuteStates(room);
      return enabled; // devolve o estado nao-mudado pra UI nao desalinhar
    }
    syncTracks(room);
    syncMuteStates(room);
    return !enabled;
  }, [syncTracks, syncMuteStates, isLocalSourceEnabled]);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const enabled = isLocalSourceEnabled(room, Track.Source.Microphone);
    try {
      await room.localParticipant.setMicrophoneEnabled(!enabled);
    } catch (err) {
      logDiagnostic({
        severity: 'error',
        type: 'toggle_mic_failed',
        message: err?.message || 'Falha ao alternar microfone',
        metadata: {
          name: err?.name,
          target: !enabled,
          roomState: room.state,
        },
      });
      syncTracks(room);
      syncMuteStates(room);
      return enabled;
    }
    syncTracks(room);
    syncMuteStates(room);
    return !enabled;
  }, [syncTracks, syncMuteStates, isLocalSourceEnabled]);

  // Semaforo evita que cliques rapidos no botao Fundo instanciem varios
  // processors simultaneamente - cada BackgroundBlur/VirtualBackground baixa o
  // modelo Mediapipe, consome GPU e compete pela mesma MediaStreamTrack. Sem a
  // trava a camera trava ou pisca ("Stream closed" nos logs do contato).
  const processorLockRef = useRef(false);
  const setProcessor = useCallback(async (processor) => {
    if (processorLockRef.current) return;
    processorLockRef.current = true;
    try {
      const room = roomRef.current;
      if (!room) return;
      const cameraPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      if (!cameraPub?.track) return;

      // Sempre para o processor atual antes de aplicar o novo, inclusive antes
      // do primeiro - cobre o caso de reaproveitamento de track entre troca de
      // camera. track-processors antigo nao fazia isso internamente e deixava
      // instancias zombie segurando GPU.
      try { await cameraPub.track.stopProcessor(); } catch { /* sem processor ativo */ }

      if (processor) {
        await cameraPub.track.setProcessor(processor);
      }
      syncTracks(room);
    } catch (err) {
      console.error('setProcessor error:', err);
      logDiagnostic({
        severity: 'error',
        type: 'processor_failed',
        message: err?.message || 'Falha ao aplicar processor de camera',
        metadata: { name: err?.name, stack: err?.stack },
      });
    } finally {
      processorLockRef.current = false;
    }
  }, [syncTracks]);

  // Alterna entre camera frontal (user) e traseira (environment). Util no mobile
  // do paciente pra mostrar lesao, area afetada etc. Em notebooks desktop com
  // uma unica camera, o facingMode eh ignorado pelo dispositivo - apenas
  // reinicializa a mesma camera.
  const switchCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = cameraFacing === 'user' ? 'environment' : 'user';
    try {
      const cameraPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const track = cameraPub?.videoTrack;
      if (track?.restartTrack) {
        await track.restartTrack({ facingMode: next });
      } else {
        await room.localParticipant.setCameraEnabled(false);
        await room.localParticipant.setCameraEnabled(true, { facingMode: next });
      }
      setCameraFacing(next);
      syncTracks(room);
    } catch (err) {
      console.error('switchCamera error:', err);
    }
  }, [cameraFacing, syncTracks]);

  useEffect(() => {
    // Volta da aba oculta (ex: medico abriu o PDF que paciente mandou, ficou
    // alguns minutos lendo, voltou). Chrome e principalmente iOS Safari:
    //   - sao agressivos em pausar/throttlar tab background
    //   - chegam a ENCERRAR o MediaStreamTrack local (camera/mic) pra economizar
    //     bateria, deixando o track.mediaStreamTrack.readyState === 'ended'
    //   - quando o usuario volta, o LiveKit track ainda existe (publicado),
    //     mas nao produz frames - outro lado fica vendo tela preta/sem audio
    // Por isso, ao voltar, checamos cada track local e restartamos se morreu.
    // O VideoPlayer cuida em paralelo de re-chamar play() no <video> pausado.
    const handleVisibility = async () => {
      const room = roomRef.current;
      if (!room || document.hidden) return;
      if (room.state !== 'connected') return;

      // Restart de tracks locais que o browser matou em background.
      try {
        const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
        const camTrack = camPub?.track;
        if (camTrack && camTrack.mediaStreamTrack?.readyState === 'ended' && !camTrack.isMuted) {
          try { await camTrack.restartTrack(); }
          catch (err) {
            logDiagnostic({
              severity: 'warn',
              type: 'track_restart_failed',
              message: err?.message || 'Falha ao restartar camera',
              metadata: { source: 'camera', name: err?.name },
            });
          }
        }

        const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
        const micTrack = micPub?.track;
        if (micTrack && micTrack.mediaStreamTrack?.readyState === 'ended' && !micTrack.isMuted) {
          try { await micTrack.restartTrack(); }
          catch (err) {
            logDiagnostic({
              severity: 'warn',
              type: 'track_restart_failed',
              message: err?.message || 'Falha ao restartar microfone',
              metadata: { source: 'microphone', name: err?.name },
            });
          }
        }
      } catch { /* defensivo: se a API mudar, nao quebra o resume */ }

      syncTracks(room);
      syncMuteStates(room);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [syncTracks, syncMuteStates]);

  useEffect(() => () => disconnect(), [disconnect]);

  return {
    localVideoTrack, remoteVideoTrack, remoteAudioTrack,
    isRemoteConnected, connectionStatus, remoteName,
    localMic, localCam, remoteMic, remoteCam, cameraFacing,
    networkMetrics, connectionEpoch,
    connect, disconnect, toggleCamera, toggleMic, setProcessor, switchCamera,
  };
}
